"use client"

import type { WindowState } from "@/components/desktop/types"
import type { HighlightedText } from "@/components/desktop/wilsonViewContextRegistry"
import type { ViewContext } from "@/schemas/wilsonViewContext.schema"
import { reportClientWarning } from "@/lib/clientReporting"

type ScreenReadModule = typeof import("@/components/desktop/screenRead.utils")
type ViewContextModule =
    typeof import("@/components/desktop/wilsonViewContext.utils")

const importScreenRead = () => import("@/components/desktop/screenRead.utils")
const importViewContext = () =>
    import("@/components/desktop/wilsonViewContext.utils")
let pendingScreenRead: Promise<ScreenReadModule> | undefined
let loadedScreenRead: ScreenReadModule | undefined
let loadedViewContext: ViewContextModule | undefined

export const loadScreenRead = (): Promise<ScreenReadModule> => {
    pendingScreenRead ??= importScreenRead().then(
        module => {
            loadedScreenRead = module
            return module
        },
        error => {
            pendingScreenRead = undefined
            // Wilson still gets the windows and the highlight, just not the
            // screen's text; the next send tries the chunk again.
            reportClientWarning({
                message: "The screen read chunk did not load",
                cause: error instanceof Error ? error : null,
                tags: {
                    surface: "wilsonViewContext",
                    action: "loadScreenRead",
                },
            })
            throw error
        }
    )
    return pendingScreenRead
}

export const preloadPageCapture = (): void => {
    // loadScreenRead reports its own failure before it rethrows.
    loadScreenRead().catch(() => undefined)
    importViewContext().then(
        module => {
            loadedViewContext = module
        },
        // A page question sent before this chunk lands still reaches Wilson;
        // Chat builds the context itself, from its own screen.
        () => undefined
    )
}

export const capturePageContext = ({
    windows,
    highlightedText,
}: {
    windows: WindowState[]
    highlightedText?: HighlightedText
}): ViewContext | undefined =>
    loadedViewContext?.buildViewContext({
        windows,
        highlightedText,
        readScreen: windowId =>
            loadedScreenRead?.readScreen(
                loadedScreenRead.screenRootOf(windowId)
            ) ?? [],
    })
